import { dateLocale, formatTime, todayCalendarDay } from '@/utils/format';

/**
 * A task's due date, said the way a person says it (ADR 0069): "Today",
 * "Tomorrow", "Yesterday", a weekday within the coming week, else a date.
 *
 * Due dates are wall-clock days with no zone (`YYYY-MM-DD`), so they are
 * compared as days, never as instants: "today" is today's calendar day in the
 * owner's chosen zone (`todayCalendarDay`), passed in so this stays pure.
 */

export type DueTone = 'overdue' | 'today' | 'soon' | 'later';

export interface DueLabel {
  text: string;
  tone: DueTone;
  /** A sentence for assistive technology: "Due today at 3:00 PM", "Overdue: due yesterday". */
  spoken: string;
}

const DAY_MS = 86_400_000;

/** Whole days from `from` to `to`, both `YYYY-MM-DD`. */
export function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY_MS);
}

/** `day` plus `n` days, as `YYYY-MM-DD`. */
export function addDays(day: string, n: number): string {
  return new Date(Date.parse(`${day}T00:00:00Z`) + n * DAY_MS).toISOString().slice(0, 10);
}

/** How far ahead "Due soon" looks: today and the next six days, overdue included. */
export const DUE_SOON_DAYS = 7;

/** The last day the "Due soon" view includes. */
export function dueSoonBefore(today: string): string {
  return addDays(today, DUE_SOON_DAYS - 1);
}

function formatDay(day: string, withYear: boolean): string {
  // Noon UTC formatted in UTC: the day itself, whatever the zone.
  return new Intl.DateTimeFormat(dateLocale(), {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric',
    ...(withYear ? { year: 'numeric' } : {}),
  }).format(new Date(`${day}T12:00:00Z`));
}

function formatWeekday(day: string): string {
  return new Intl.DateTimeFormat(dateLocale(), { timeZone: 'UTC', weekday: 'long' }).format(
    new Date(`${day}T12:00:00Z`),
  );
}

/** "15:00" → "3:00 PM" in the chosen locale. */
export function formatDueTime(time: string): string {
  const [hours, minutes] = time.split(':').map(Number);
  return new Intl.DateTimeFormat(dateLocale(), {
    timeZone: 'UTC',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(Date.UTC(2000, 0, 1, hours ?? 0, minutes ?? 0)));
}

export function dueLabel(dueDate: string, dueTime: string | null, today: string): DueLabel {
  const offset = daysBetween(today, dueDate);
  let day: string;
  if (offset === 0) day = 'Today';
  else if (offset === 1) day = 'Tomorrow';
  else if (offset === -1) day = 'Yesterday';
  else if (offset > 1 && offset < 7) day = formatWeekday(dueDate);
  else day = formatDay(dueDate, dueDate.slice(0, 4) !== today.slice(0, 4));

  const time = dueTime ? formatDueTime(dueTime) : '';
  const text = time ? `${day}, ${time}` : day;
  const tone: DueTone =
    offset < 0 ? 'overdue' : offset === 0 ? 'today' : offset < DUE_SOON_DAYS ? 'soon' : 'later';
  const lower = offset >= -1 && offset <= 1 ? day.toLowerCase() : offset > 1 && offset < 7 ? day : `on ${day}`;
  const spokenDue = `due ${lower}${time ? ` at ${time}` : ''}`;
  return {
    text,
    tone,
    spoken: tone === 'overdue' ? `Overdue: ${spokenDue}` : spokenDue.charAt(0).toUpperCase() + spokenDue.slice(1),
  };
}

export interface ReminderLabel {
  text: string;
  /** "Reminder today at 2:50 PM". */
  spoken: string;
}

/**
 * When a task's reminder rings, said like its due date: "Today, 2:50 PM",
 * "Tomorrow, 9:00 AM", a weekday, a date. `remindAt` is an instant (the
 * server worked it out in the owner's zone), so its day and time are read in
 * the chosen zone and locale, as every other time in the app is.
 */
export function reminderLabel(remindAt: string, today: string): ReminderLabel {
  const when = new Date(remindAt);
  const day = dueLabel(todayCalendarDay(when), null, today);
  const time = formatTime(when);
  const spokenDay = day.spoken.replace(/^(Overdue: )?[Dd]ue /, '');
  return { text: `${day.text}, ${time}`, spoken: `Reminder ${spokenDay} at ${time}` };
}
